import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaUsers, FaKey, FaDesktop, FaBan, FaSignOutAlt, FaTachometerAlt, FaPaperPlane, FaShieldAlt } from 'react-icons/fa';
import Login from './Login';
import Dashboard from './Dashboard';
import Users from './Users';
import Licenses from './Licenses';
import DevicesList from './Devices';
import Apps from './Apps';
import Messaging from './Messaging';
import { io } from 'socket.io-client';
import Modal from './components/Modal';
import api from './api';
import './App.css';

const socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000');

function App() {
    const [auth, setAuth] = useState(false);
    const [popup, setPopup] = useState(null); // { message, title }
    const [isProtectionModalOpen, setProtectionModalOpen] = useState(false);

    useEffect(() => {
        if (localStorage.getItem('token')) {
            setAuth(true);
            // Join rooms for all licenses this user manages/owns
            api.get('/licenses').then(({ data }) => {
                data.forEach(lic => {
                    socket.emit('join_room', lic.license_key);
                });
            }).catch(() => {});
        }
    }, [auth]);

    useEffect(() => {
        socket.on('popup_message', (data) => {
            setPopup({ message: data.message, title: 'SYSTEM ALERT' });
        });

        socket.on('global_notification', (data) => {
            toast(data.message, { icon: '🔔' });
        });

        return () => {
            socket.off('popup_message');
            socket.off('global_notification');
        };
    }, []);

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuth(false);
    };

    const handleBulkProtection = async (bypassStatus) => {
        if (!window.confirm(`Are you sure you want to ${bypassStatus ? 'BYPASS' : 'ACTIVATE'} protection for ALL licenses in the system?`)) return;
        try {
            await api.post('/licenses/bulk-protection', { bypassStatus });
            toast.success(`System-wide protection ${bypassStatus ? 'disabled/bypassed' : 'ACTIVATED'}`);
            setProtectionModalOpen(false);
            window.location.reload(); // Refresh to update all states
        } catch (err) {
            toast.error('Failed to update system protection');
        }
    };

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    return (
        <Router>
            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        background: '#1f2833',
                        color: '#66fcf1',
                        border: '1px solid #45a29e',
                    },
                }}
            />
            <div className="App">
                {auth && <Sidebar logout={logout} user={user} setProtectionModalOpen={setProtectionModalOpen} />}
                <div className="content">
                    <Routes>
                        <Route path="/login" element={!auth ? <Login setAuth={setAuth} /> : <Navigate to="/" />} />
                        <Route path="/" element={auth ? <Dashboard /> : <Navigate to="/login" />} />
                        <Route path="/users" element={auth && (user.role === 'super_admin' || user.perm_users === 1 || user.perm_users === true) ? <Users /> : <Navigate to="/" />} />
                        <Route path="/licenses" element={auth ? <Licenses /> : <Navigate to="/login" />} />
                        <Route path="/devices" element={auth ? <DevicesList /> : <Navigate to="/login" />} />
                        <Route path="/apps" element={auth ? <Apps /> : <Navigate to="/login" />} />
                        <Route path="/messaging" element={auth && (user.role === 'super_admin' || user.perm_messaging === 1 || user.perm_messaging === true) ? <Messaging /> : <Navigate to="/" />} />
                    </Routes>
                </div>
            </div>

            <Modal isOpen={!!popup} onClose={() => setPopup(null)} title={popup?.title}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <p style={{ fontSize: '1.2rem', color: '#c5c6c7', lineHeight: '1.6' }}>{popup?.message}</p>
                    <button onClick={() => setPopup(null)} style={{ marginTop: '20px', width: 'auto', background: '#66fcf1', color: '#0b0c10' }}>Dismiss</button>
                </div>
            </Modal>

            <Modal isOpen={isProtectionModalOpen} onClose={() => setProtectionModalOpen(false)} title="Security Control Center">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <FaShieldAlt style={{ fontSize: '4rem', color: '#66fcf1', marginBottom: '20px' }} />
                    <h3 style={{ color: '#fff' }}>Global System Protection</h3>
                    <p style={{ color: '#aaa', marginBottom: '30px' }}>Apply immediate security changes to all registered licenses and connected devices.</p>
                    
                    <button 
                        onClick={() => handleBulkProtection(false)} 
                        style={{ width: '100%', padding: '15px', background: '#45a29e', color: '#fff', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '15px' }}
                    >
                        <FaShieldAlt /> ACTIVATE ALL PROTECTION
                    </button>
                    
                    <button 
                        onClick={() => handleBulkProtection(true)} 
                        style={{ width: '100%', padding: '15px', background: 'transparent', border: '1px solid #ff4d4d', color: '#ff4d4d', fontSize: '1.1rem' }}
                    >
                        BYPASS ALL PROTECTION (UNSECURED)
                    </button>
                    
                    <p style={{ fontSize: '0.8rem', color: '#555', marginTop: '20px' }}>* Activating protection turns OFF 'Global Bypass' for all licenses.</p>
                </div>
            </Modal>
        </Router>
    );
}

const Sidebar = ({ logout, user: propUser, setProtectionModalOpen }) => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path ? 'active' : '';

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role === 'super_admin';
    const hasPerm = (perm) => isSuperAdmin || user[perm] === 1 || user[perm] === true;

    // A regular user can see their own stuff. Admins need specific permissions.
    const canSeeUsers = hasPerm('perm_users');
    const canSeeMessaging = hasPerm('perm_messaging');
    const canSeeApps = user.role === 'user' || hasPerm('perm_apps');
    const canSeeLicenses = user.role === 'user' || hasPerm('perm_licenses');
    const canSeeDevices = user.role === 'user' || hasPerm('perm_devices');

    return (
        <nav className="navbar">
            <div className="logo">iTech365 <br /><span style={{ fontSize: '0.8rem', color: '#45a29e' }}>Guardian</span></div>
            <div className="links">
                <Link to="/" className={isActive('/')}><FaTachometerAlt /> Dashboard</Link>
                
                {canSeeUsers && <Link to="/users" className={isActive('/users')}><FaUsers /> Users</Link>}
                {canSeeMessaging && <Link to="/messaging" className={isActive('/messaging')}><FaPaperPlane /> Messaging</Link>}
                
                {canSeeLicenses && <Link to="/licenses" className={isActive('/licenses')}><FaKey /> Licenses</Link>}
                {canSeeDevices && <Link to="/devices" className={isActive('/devices')}><FaDesktop /> Devices</Link>}
                
                {hasPerm('perm_licenses') && (
                    <button 
                        onClick={() => setProtectionModalOpen(true)}
                        style={{ background: 'rgba(102, 252, 241, 0.1)', border: '1px solid #66fcf1', color: '#66fcf1', margin: '10px 20px', width: 'calc(100% - 40px)', padding: '10px' }}
                    >
                        <FaShieldAlt /> Protection
                    </button>
                )}
                
                {canSeeApps && <Link to="/apps" className={isActive('/apps')}><FaBan /> {hasPerm('perm_apps') ? 'Blocked Apps' : 'Security'}</Link>}
            </div>
            <div className="user-profile" style={{ padding: '0 20px', marginBottom: '10px', fontSize: '0.8rem' }}>
                <div style={{ color: '#66fcf1' }}>{user.name}</div>
                <div style={{ color: '#45a29e', fontSize: '0.7rem' }}>{user.role ? String(user.role).toUpperCase() : ''}</div>
            </div>
            <button onClick={logout} className="logout-btn"><FaSignOutAlt /> Logout</button>
        </nav>
    );
};

export default App;
